import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import TourGuide from './components/TourGuide';
import InstallButton from './components/InstallButton';

// Import pages essentielles (chargées immédiatement)
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import Dashboard from './pages/Dashboard';

// Lazy load des autres pages (chargées à la demande)
const MachinesPage = lazy(() => import('./pages/MachinesPage'));
const MachineDetailPage = lazy(() => import('./pages/MachineDetailPage'));
const LeadsPage = lazy(() => import('./pages/LeadsPageNew'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePageNew'));
const UsersPage = lazy(() => import('./pages/UsersPageNew'));
const FranchisesPage = lazy(() => import('./pages/FranchisesPageNew'));
const RichardPage = lazy(() => import('./pages/RichardPage'));
const AnnuairePage = lazy(() => import('./pages/AnnuairePageNew'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const ConfigPage = lazy(() => import('./pages/ConfigPage'));
const SupportPage = lazy(() => import('./pages/SupportPageNew'));
const LogisticsPage = lazy(() => import('./pages/LogisticsPage'));
const TrainingPage = lazy(() => import('./pages/TrainingPage'));
const FormBuilderPage = lazy(() => import('./pages/FormBuilderPageNew'));
const OperatorTourPage = lazy(() => import('./pages/OperatorTourPage'));
const FeasibilityStudyPage = lazy(() => import('./pages/FeasibilityStudyPage'));
const SimulatorPage = lazy(() => import('./pages/SimulatorPage'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-black">
    <div className="text-white text-xl">Chargement...</div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.baseURL = API;

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }

    // Register Service Worker for PWA
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/service-worker.js')
          .then((registration) => {
            console.log('SW registered: ', registration);
          })
          .catch((registrationError) => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data);
      
      // Check if user has seen the tour
      const hasSeenTour = localStorage.getItem(`tour_completed_${response.data.id}`);
      if (!hasSeenTour) {
        setTimeout(() => setShowTour(true), 1000);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setLoading(false);
    delete axios.defaults.headers.common['Authorization'];
  };

  const handleTourExit = () => {
    setShowTour(false);
    if (user) {
      localStorage.setItem(`tour_completed_${user.id}`, 'true');
    }
  };

  const handleRestartTour = () => {
    if (user) {
      localStorage.removeItem(`tour_completed_${user.id}`);
      // Force re-render du TourGuide
      setShowTour(false);
      setTimeout(() => setShowTour(true), 100);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <BrowserRouter>
        {user && showTour && <TourGuide enabled={showTour} onExit={handleTourExit} />}
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" />} />
            <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to="/" />} />
            <Route path="/" element={user ? <Dashboard onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/machines" element={user ? <MachinesPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/machines/:machineId" element={user ? <MachineDetailPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/leads" element={user ? <LeadsPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/leads/forms" element={user ? <FormBuilderPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/leaderboard" element={user ? <LeaderboardPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/analytics" element={user ? <AnalyticsPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/shop" element={user ? <ShopPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/richard" element={user ? <RichardPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/annuaire" element={user ? <AnnuairePage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/knowledge" element={user ? <KnowledgeBasePage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/maintenance" element={user ? <MaintenancePage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/franchises" element={user ? <FranchisesPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/users" element={user ? <UsersPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/admin" element={user ? <AdminPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/config" element={user ? <ConfigPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/support" element={user ? <SupportPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/logistics" element={user ? <LogisticsPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/training" element={user ? <TrainingPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/forms" element={user ? <FormBuilderPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/feasibility-study" element={user ? <FeasibilityStudyPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/simulator" element={user ? <SimulatorPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
            <Route path="/operator-tour" element={user ? <OperatorTourPage onRestartTour={handleRestartTour} /> : <Navigate to="/login" />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <InstallButton />
      <Toaster 
        position="top-right" 
        expand={false} 
        richColors 
        theme="light"
        toastOptions={{
          style: {
            color: '#1a1a1a',
            fontWeight: '500'
          }
        }}
      />
    </AuthContext.Provider>
  );
}

export default App;
